/**
 * Pure functional-form types, enum arrays, and type guards — no DB imports, so
 * this module is safe to import from client components (weekly-form pickers,
 * profile edit, flyover). The DB-backed query/mutation helpers live in
 * `form-data.ts` (server-only); the field/tone/derivation contract lives in
 * `form-registry.ts`.
 *
 * The enum arrays here mirror the `FORM_*` arrays in `server/db/schema/forms.ts`
 * (the observation-notes precedent: a pure types module duplicates the schema
 * enum so client bundles never pull in Drizzle). The two are structurally
 * identical literal unions, so the data layer fails to compile if they drift.
 */

export const FORM_SUPPORT_TRACKS = [
  "onboarding_intensive_support",
  "ongoing_supervision",
] as const;
export type FormSupportTrack = (typeof FORM_SUPPORT_TRACKS)[number];

export const FORM_MEETING_CADENCES = [
  "weekly",
  "biweekly",
  "monthly",
  "as_needed",
] as const;
export type FormMeetingCadence = (typeof FORM_MEETING_CADENCES)[number];

export const FORM_EMPLOYMENT_LOADS = ["full_time", "part_time"] as const;
export type FormEmploymentLoad = (typeof FORM_EMPLOYMENT_LOADS)[number];

export const FORM_REVIEW_STATUSES = [
  "not_scheduled",
  "scheduled",
  "completed",
] as const;
export type FormReviewStatus = (typeof FORM_REVIEW_STATUSES)[number];

export const FORM_TYPES = ["weekly_form", "monthly_checkpoint"] as const;
export type FormType = (typeof FORM_TYPES)[number];

export const FORM_VARIANTS = [
  "onboarding_intensive_support",
  "ongoing_supervision",
] as const;
export type FormVariant = (typeof FORM_VARIANTS)[number];

export const FORM_STATUSES = [
  "maintenance_rock_star",
  "active_growth",
  "watch_list",
  "at_risk",
] as const;
export type FormStatus = (typeof FORM_STATUSES)[number];

export const FORM_TRAJECTORIES = ["improving", "stable", "declining"] as const;
export type FormTrajectory = (typeof FORM_TRAJECTORIES)[number];

export const FORM_CONFIDENCE_LEVELS = [
  "high",
  "moderate",
  "low",
  "concerning",
] as const;
export type FormConfidence = (typeof FORM_CONFIDENCE_LEVELS)[number];

export const FORM_COUNSELING_DEVELOPMENT_LEVELS = [
  "on_track",
  "minor_gaps",
  "significant_gaps",
] as const;
export type FormCounselingDevelopment =
  (typeof FORM_COUNSELING_DEVELOPMENT_LEVELS)[number];

export const FORM_CLINICAL_DIRECTOR_ATTENTION_LEVELS = [
  "no",
  "monitor",
  "yes",
] as const;
export type FormClinicalDirectorAttention =
  (typeof FORM_CLINICAL_DIRECTOR_ATTENTION_LEVELS)[number];

function isMember<T extends string>(
  values: readonly T[],
  value: unknown,
): value is T {
  return typeof value === "string" && (values as readonly string[]).includes(value);
}

export function isFormSupportTrack(value: unknown): value is FormSupportTrack {
  return isMember(FORM_SUPPORT_TRACKS, value);
}
export function isFormMeetingCadence(value: unknown): value is FormMeetingCadence {
  return isMember(FORM_MEETING_CADENCES, value);
}
export function isFormEmploymentLoad(value: unknown): value is FormEmploymentLoad {
  return isMember(FORM_EMPLOYMENT_LOADS, value);
}
export function isFormReviewStatus(value: unknown): value is FormReviewStatus {
  return isMember(FORM_REVIEW_STATUSES, value);
}
export function isFormType(value: unknown): value is FormType {
  return isMember(FORM_TYPES, value);
}
export function isFormVariant(value: unknown): value is FormVariant {
  return isMember(FORM_VARIANTS, value);
}
export function isFormStatus(value: unknown): value is FormStatus {
  return isMember(FORM_STATUSES, value);
}
export function isFormTrajectory(value: unknown): value is FormTrajectory {
  return isMember(FORM_TRAJECTORIES, value);
}
export function isFormConfidence(value: unknown): value is FormConfidence {
  return isMember(FORM_CONFIDENCE_LEVELS, value);
}
export function isFormCounselingDevelopment(
  value: unknown,
): value is FormCounselingDevelopment {
  return isMember(FORM_COUNSELING_DEVELOPMENT_LEVELS, value);
}
export function isFormClinicalDirectorAttention(
  value: unknown,
): value is FormClinicalDirectorAttention {
  return isMember(FORM_CLINICAL_DIRECTOR_ATTENTION_LEVELS, value);
}

/**
 * The narrative half of a weekly form, stored in `form_snapshots.response_json`
 * as text (JSON.stringify / parse + this guard, per the `meeting_notes.bodyJson`
 * precedent). The five queryable enum answers stay in their own snapshot columns
 * so they can be filtered without parsing JSON; `response_json` carries the free
 * text and may optionally mirror the enum answers for a self-contained record.
 *
 * All four narrative fields are optional (free text is not required per variant).
 */
export interface FormResponseJson {
  biggestWin?: string | null;
  biggestBarrier?: string | null;
  coachingFocus?: string | null;
  actionPlan?: string | null;
  /**
   * Optional mirror of the enum answers captured at save time. Not the query
   * source of truth (the snapshot columns are) — present only so a raw
   * response_json blob is human-readable on its own.
   */
  enumAnswers?: Record<string, string>;
}

/** The four free-text keys, in display order. Mirrors the registry text fields. */
export const FORM_NARRATIVE_KEYS = [
  "biggestWin",
  "biggestBarrier",
  "coachingFocus",
  "actionPlan",
] as const;
export type FormNarrativeKey = (typeof FORM_NARRATIVE_KEYS)[number];

/**
 * Structural guard for a parsed `response_json` value: an object (not array)
 * whose known narrative fields, when present, are string | null and whose
 * `enumAnswers`, when present, is a string→string record. Unknown keys are
 * tolerated so older/newer shapes round-trip without throwing.
 */
export function isFormResponseJson(value: unknown): value is FormResponseJson {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  for (const key of FORM_NARRATIVE_KEYS) {
    const field = record[key];
    if (field !== undefined && field !== null && typeof field !== "string") {
      return false;
    }
  }
  const enumAnswers = record.enumAnswers;
  if (enumAnswers !== undefined) {
    if (!enumAnswers || typeof enumAnswers !== "object" || Array.isArray(enumAnswers)) {
      return false;
    }
    for (const answer of Object.values(enumAnswers as Record<string, unknown>)) {
      if (typeof answer !== "string") return false;
    }
  }
  return true;
}

/** An empty narrative body — the starting point for a fresh weekly form. */
export function emptyFormResponseJson(): FormResponseJson {
  return {};
}

/**
 * A single functional-form snapshot projected for the UI, with author and
 * supervisor names resolved and `response_json` parsed. Returned by the
 * `form-data` list/get/create/update helpers.
 */
export interface FormSnapshotData {
  id: string;
  rdId: string;
  formType: FormType;
  formVariant: FormVariant;
  formVersion: number;
  periodStartDate: string;
  periodEndDate: string;
  status: FormStatus | null;
  trajectory: FormTrajectory | null;
  overallConfidence: FormConfidence | null;
  counselingDevelopment: FormCounselingDevelopment | null;
  clinicalDirectorAttention: FormClinicalDirectorAttention | null;
  response: FormResponseJson;
  createdAt: string;
  updatedAt: string;
  authorUserId: string;
  authorName: string;
  supervisorUserId: string;
  supervisorName: string;
}

/** Editable profile fields for the upsert path. `supportTrack` is always set. */
export interface UpsertFormProfileInput {
  supportTrack: FormSupportTrack;
  startDate?: string | null;
  meetingCadence?: FormMeetingCadence | null;
  employmentLoad?: FormEmploymentLoad | null;
  ninetyDayReviewStatus?: FormReviewStatus | null;
  ninetyDayReviewDate?: string | null;
  annualEvaluationStatus?: FormReviewStatus | null;
  annualEvaluationDate?: string | null;
  contractMinimumHours?: number | null;
  currentTargetHours?: number | null;
  profileNotes?: string | null;
  socialNotes?: string | null;
}

/**
 * Full wire shape of PUT /api/rds/:rdId/forms/profile. The route replaces the
 * whole row, so every writer must send every field — required-but-nullable
 * makes omitting one (and silently wiping its stored value) a compile error.
 */
export interface FormProfilePutPayload {
  supportTrack: FormSupportTrack;
  startDate: string | null;
  meetingCadence: FormMeetingCadence | null;
  employmentLoad: FormEmploymentLoad | null;
  ninetyDayReviewStatus: FormReviewStatus | null;
  ninetyDayReviewDate: string | null;
  annualEvaluationStatus: FormReviewStatus | null;
  annualEvaluationDate: string | null;
  contractMinimumHours: number | null;
  currentTargetHours: number | null;
  profileNotes: string | null;
  socialNotes: string | null;
}

/** Input for creating a snapshot. The route resolves author/supervisor + period. */
export interface CreateFormSnapshotInput {
  rdId: string;
  authorUserId: string;
  supervisorUserId: string;
  formType: FormType;
  formVariant: FormVariant;
  formVersion: number;
  periodStartDate: string;
  periodEndDate: string;
  status?: FormStatus | null;
  trajectory?: FormTrajectory | null;
  overallConfidence?: FormConfidence | null;
  counselingDevelopment?: FormCounselingDevelopment | null;
  clinicalDirectorAttention?: FormClinicalDirectorAttention | null;
  response: FormResponseJson;
}

/** Patch for editing an existing snapshot (variant Change link + answers). */
export interface UpdateFormSnapshotInput {
  formVariant?: FormVariant;
  formVersion?: number;
  status?: FormStatus | null;
  trajectory?: FormTrajectory | null;
  overallConfidence?: FormConfidence | null;
  counselingDevelopment?: FormCounselingDevelopment | null;
  clinicalDirectorAttention?: FormClinicalDirectorAttention | null;
  response?: FormResponseJson;
}
